import { observer } from 'mobx-react';
import * as React from 'react';
import { SliderView } from 'elc-slider';
import styled, { css } from 'styled-components';
import { ProductBadge } from '../product-badge/ProductBadge';
import { serviceNames, diContainer } from '../../../service-setup/diContainer';
import {
    VERTICAL_SWIPE_SETTINGS,
    HORIZONTAL_SWIPE_SETTINGS,
    DEFAULT_IMAGE_CAROUSEL_SETTINGS,
    DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS
} from '../../constants/ImageThumbnailCarouselSettings';
import { ContentSpacing, breakpoint, Breakpoints } from 'elc-base-theme';
import { Desktop, Mobile } from 'elc-layouts';
import { ProductImages } from 'elc-images';
import { observable } from 'mobx';
import Slider from 'react-slick';
import { classes } from '../../utils/Classes';
import { ProductZoomButton } from '../product-zoom/ProductZoomButton';
import { ICarouselSettings } from '../../interfaces/IProdcat';
import { ProductThumbnailsCarousel } from './ProductThumbnailsCarousel';
import { IOrderable } from '../../../exported/product-full-order/IProductFullOrderable';
import { ProductFullViewController } from '../../controllers/ProductFullViewController';
import { ProductFullOrderableViewController } from '../../controllers/ProductFullOrderableViewController';

export interface IProductFullCarouselConfig {
    enableZoom?: boolean;
    displayBadgeOnCurrentColumn?: boolean;
    imageCarouselSettings?: ICarouselSettings;
    enableProgressiveImages?: boolean;
}

export interface IProductSlider {
    isThumbnailsSliderVertical: boolean;
    showThumbnails?: boolean;
}

export interface IProductImage {
    src: string;
    alt: string;
    assetType?: string;
}

export interface IProductFullCarousel extends IOrderable {
    config: IProductFullCarouselConfig;
    customSizeImages: IProductImage[];
    smallImages: IProductImage[];
    onClick?(): void;
    className?: string;
}

export const StyledSliderSection = styled.div`
    position: relative;
    margin: 0 auto;
    width: ${({ showThumbnails }: IProductSlider) => (showThumbnails ? '100%' : '60%')};
    display: flex;
    flex-direction: ${props => (props.isThumbnailsSliderVertical ? 'row' : 'column-reverse')};

    .elc-product-brief-wrapper {
        margin: 0 ${ContentSpacing.space4};
    }

    .slick-dots li {
        margin: ${ContentSpacing.space4};
    }

    .slick-dots {
        ${({ isThumbnailsSliderVertical }: IProductSlider) =>
            breakpoint('desktop')`
            ${isThumbnailsSliderVertical &&
                css`
                    margin-left: -40%;
                    display: flex;
                    flex-direction: column;
                `}
            `}
        padding: ${ContentSpacing.space20};
    }
`;

@observer
export class ProductFullCarousel extends React.Component<IProductFullCarousel> {
    private productFullViewController:
        | ProductFullViewController
        | ProductFullOrderableViewController;
    public static defaultProps: IProductFullCarousel;
    @observable private carouselRef: Slider;
    @observable private thumbnailsCarouselRef: Slider;

    constructor(props: IProductFullCarousel) {
        super(props);
        this.productFullViewController = props.isOrderable
            ? diContainer.get(serviceNames.productFullOrderableViewController)
            : diContainer.get(serviceNames.productFullViewController);
    }

    public setCarouselRef = (productImagesCarousel: Slider) => {
        if (!this.carouselRef) {
            this.carouselRef = productImagesCarousel;
        }

        return this.carouselRef;
    };

    public setThumbnailsCarouselRef = (thumbnailImagesCarousel: Slider) => {
        if (!this.thumbnailsCarouselRef) {
            this.thumbnailsCarouselRef = thumbnailImagesCarousel;
        }

        return this.thumbnailsCarouselRef;
    };

    public onImageLoaded = () => {
        window.dispatchEvent(new Event('resize'));
    };

    public render() {
        let desktopCarouselSettings = {};
        const { smallImages, customSizeImages } = this.props;
        const { data } = this.productFullViewController;
        const { product, selectedSku } = data;
        const {
            enableZoom,
            imageCarouselSettings: imageCarouselConfigSettings,
            enableProgressiveImages
        } = this.props.config;
        const thumbnailsCarouselConfigSettings =
            imageCarouselConfigSettings && imageCarouselConfigSettings.thumbnailsCarouselSettings;
        const sliderItems = customSizeImages.map((customSizeImage, key) => {
            const imageProps = {
                imgPath: customSizeImage.src,
                altText: customSizeImage.alt,
                onImageLoaded: this.onImageLoaded,
                progressiveLoad: enableProgressiveImages
            };

            const productImageBadgeWrapper = classes('product-image-badge-wrapper');
            const hideImageBadge =
                imageCarouselConfigSettings && imageCarouselConfigSettings.hideImageBadge;
            const productBadgeComponent = !hideImageBadge && (
                <ProductBadge product={product} selectedSku={selectedSku} />
            );
            const isFirstImage = key === 0;

            return enableZoom ? (
                <ProductZoomButton key={key} index={key}>
                    <ProductImages {...imageProps} />
                </ProductZoomButton>
            ) : (
                <div className={productImageBadgeWrapper}>
                    {isFirstImage && productBadgeComponent}
                    <ProductImages key={key} {...imageProps} />
                </div>
            );
        });

        const imageCarouselSettings = {
            ...DEFAULT_IMAGE_CAROUSEL_SETTINGS,
            ...imageCarouselConfigSettings,
            showThumbnails:
                imageCarouselConfigSettings && imageCarouselConfigSettings.showThumbnails
        };

        const thumbnailsCarouselSettings = {
            ...DEFAULT_THUMBNAILS_CAROUSEL_SETTINGS,
            ...thumbnailsCarouselConfigSettings
        };

        if (imageCarouselSettings.vertical) {
            desktopCarouselSettings = {
                ...VERTICAL_SWIPE_SETTINGS,
                ...imageCarouselSettings
            };
        } else {
            desktopCarouselSettings = {
                ...HORIZONTAL_SWIPE_SETTINGS,
                ...imageCarouselSettings
            };
        }

        const carouselWrapperClassNames = classes('carousel-section-wrapper');

        return (
            <StyledSliderSection
                isThumbnailsSliderVertical={thumbnailsCarouselSettings.vertical}
                showThumbnails={imageCarouselSettings.showThumbnails}
                className={carouselWrapperClassNames}
            >
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() => (
                        <>
                            <SliderView
                                sliderSettings={imageCarouselSettings}
                                items={sliderItems}
                            />
                        </>
                    )}
                </Mobile>
                {!imageCarouselSettings.showThumbnails && (
                    <Desktop minWidth={Breakpoints.desktop}>
                        {() => (
                            <>
                                <SliderView
                                    sliderSettings={desktopCarouselSettings}
                                    items={sliderItems}
                                />
                            </>
                        )}
                    </Desktop>
                )}
                {imageCarouselSettings.showThumbnails && (
                    <ProductThumbnailsCarousel
                        config={this.props.config}
                        sliderImages={smallImages}
                        desktopCarouselSettings={desktopCarouselSettings}
                        isThumbnailsSliderVertical={thumbnailsCarouselSettings.vertical}
                        sliderItems={sliderItems}
                        carouselRef={this.carouselRef}
                        thumbnailsCarouselRef={this.thumbnailsCarouselRef}
                        setCarouselRef={this.setCarouselRef}
                        setThumbnailsCarouselRef={this.setThumbnailsCarouselRef}
                    />
                )}
            </StyledSliderSection>
        );
    }
}

ProductFullCarousel.defaultProps = {
    config: {},
    customSizeImages: [],
    smallImages: []
};
